/**
 * Offer Desk apply disclosures — student-facing labels for apply mode,
 * support tier, and named ATS. Maps internal backend keys only.
 */

export const APPLY_MODE_LABELS = {
  one_click: 'One-click',
  fast_apply: 'Fast apply',
  manual: 'Manual',
};

export const PORTAL_LABELS = {
  greenhouse: 'Greenhouse',
  workday: 'Workday',
  taleo: 'Taleo',
  oracle_cloud: 'Oracle Cloud',
  recruitee: 'Recruitee',
  smartrecruiters: 'SmartRecruiters',
  ashby: 'Ashby',
  lever: 'Lever',
  workable: 'Workable',
  successfactors: 'SuccessFactors',
  personio: 'Personio',
  bamboohr: 'BambooHR',
  icims: 'iCIMS',
  phenom: 'Phenom',
  join: 'join.com',
  eightfold: 'Eightfold',
  unknown: 'Employer portal',
  unsupported: 'Unsupported',
};

/** Compact student labels: Full · Assisted · Guided · Blocked */
const TIER_TO_COMPACT = {
  full_automation: 'Full',
  full_autofill_ready: 'Full',
  assisted_automation: 'Assisted',
  ready_with_review: 'Assisted',
  partial_autofill: 'Assisted',
  guided_handoff: 'Guided',
  blocked_partial: 'Blocked',
  blocked: 'Blocked',
  unsupported: 'Blocked',
};

export function applyModeLabel(mode) {
  if (!mode) return 'Manual';
  if (APPLY_MODE_LABELS[mode]) return APPLY_MODE_LABELS[mode];
  const m = String(mode).toLowerCase().replace(/\s+/g, '_');
  if (m.includes('one_click') || m === 'one-click') return 'One-click';
  if (m.includes('fast')) return 'Fast apply';
  if (m.includes('manual')) return 'Manual';
  return APPLY_MODE_LABELS[m] || String(mode).replace(/_/g, ' ');
}

export function portalLabel(portalType) {
  if (!portalType) return null;
  return PORTAL_LABELS[portalType] || String(portalType).replace(/_/g, ' ');
}

/** Resolve the best support-tier key from a job or apply packet. */
export function resolveSupportTierKey(source = {}) {
  if (source.portal_type === 'greenhouse') {
    return (
      source.greenhouse_support_tier
      || source.greenhouse_certification?.support_tier
      || source.apply_support_tier
      || null
    );
  }
  return (
    source.apply_support_tier
    || source.greenhouse_support_tier
    || source.greenhouse_certification?.support_tier
    || null
  );
}

export function supportTierCompact(key) {
  if (!key) return null;
  return TIER_TO_COMPACT[key] || null;
}

export function supportTierStyle(compactOrKey) {
  const compact = TIER_TO_COMPACT[compactOrKey] || compactOrKey;
  switch (compact) {
    case 'Full':
      return { bg: 'var(--tandm-success-bg)', border: 'var(--tandm-success-border)', color: 'var(--tandm-success-fg)' };
    case 'Assisted':
      return { bg: 'var(--tandm-warning-bg)', border: 'var(--tandm-warning-border)', color: 'var(--tandm-warning-fg)' };
    case 'Guided':
      return { bg: 'var(--tandm-info-bg)', border: 'var(--tandm-info-border)', color: 'var(--tandm-info-fg)' };
    case 'Blocked':
      return { bg: 'var(--tandm-danger-bg)', border: 'var(--tandm-danger-border)', color: 'var(--tandm-danger-fg)' };
    default:
      return { bg: 'var(--tandm-line-soft)', border: 'var(--tandm-line)', color: 'var(--tandm-muted)' };
  }
}

export function applyModeStyle(mode) {
  if (mode === 'one_click') {
    return { bg: 'var(--tandm-success-bg)', color: 'var(--tandm-success-fg)' };
  }
  if (mode === 'fast_apply') {
    return { bg: 'var(--tandm-accent-subtle)', color: 'var(--tandm-accent-deep)' };
  }
  return { bg: 'var(--tandm-line-soft)', color: 'var(--tandm-body)' };
}

/** Certified coverage % when present (Greenhouse). */
export function certifiedCoverage(source = {}) {
  const score = source.greenhouse_coverage_score;
  if (score === null || score === undefined) return null;
  return score;
}
